import { useCallback, useEffect, useState } from "react";
import { Monitor, Moon, Sun } from "lucide-react";

/* Thème clair / sombre / système.
   L'attribut data-theme est posé sur <html> avec la valeur résolue ; le mode
   « système » retire l'attribut et laisse prefers-color-scheme décider, ce que
   admin.css prend en charge. Le choix est mémorisé par navigateur. */

const STORE = "perform.admin.theme";
const MODES = [
  { id: "light", label: "Clair", icon: Sun },
  { id: "dark", label: "Sombre", icon: Moon },
  { id: "system", label: "Système", icon: Monitor },
];

export function readThemeMode() {
  try {
    const value = window.localStorage.getItem(STORE);
    return MODES.some(m => m.id === value) ? value : "system";
  } catch {
    return "system";
  }
}

export function applyTheme(mode) {
  const root = document.documentElement;
  if (mode === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", mode);
}

export function useTheme() {
  const [mode, setMode] = useState(readThemeMode);

  useEffect(() => {
    applyTheme(mode);
    try {
      window.localStorage.setItem(STORE, mode);
    } catch {
      /* Stockage refusé : le thème reste valable pour la session. */
    }
  }, [mode]);

  // En mode système, on suit les changements de préférence de l'OS sans recharger.
  useEffect(() => {
    if (mode !== "system" || !window.matchMedia) return undefined;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => applyTheme("system");
    media.addEventListener?.("change", sync);
    return () => media.removeEventListener?.("change", sync);
  }, [mode]);

  const cycle = useCallback(() => {
    setMode(current => MODES[(MODES.findIndex(m => m.id === current) + 1) % MODES.length].id);
  }, []);

  return { mode, setMode, cycle };
}

export default function ThemeToggle({ mode, onChange }) {
  const current = MODES.find(m => m.id === mode) || MODES[2];
  const Icon = current.icon;
  const next = MODES[(MODES.indexOf(current) + 1) % MODES.length];

  return (
    <button
      type="button"
      className="adm-btn is-ghost is-icon"
      onClick={() => onChange(next.id)}
      title={`Thème : ${current.label} — cliquer pour passer en ${next.label.toLowerCase()}`}
      aria-label={`Thème ${current.label}. Passer en thème ${next.label.toLowerCase()}`}
      data-testid="theme-toggle"
    >
      <Icon size={15} />
    </button>
  );
}
